import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { durations, easings } from '../tokens';
import { useReducedMotion } from '../motion/useReducedMotion';
import { cx } from '../internal/cx';

export type FlipCardProps = {
  /** Face visible : `false` = front, `true` = back. */
  flipped: boolean;
  front: ReactNode;
  back: ReactNode;
  /** Axe de rotation (défaut `y`). */
  axis?: 'x' | 'y';
  /** Durée en ms (défaut `--dur-flip` = 480). */
  duration?: number;
  className?: string;
};

/**
 * Carte à retournement 3D (base de la tuile MOTUS). Flip `rotateY/X` en
 * `--dur-flip` ; reduced-motion → simple cross-fade, le contenu reste lisible.
 * La face cachée est `aria-hidden` (une seule face exposée aux lecteurs d'écran).
 */
export function FlipCard({
  flipped,
  front,
  back,
  axis = 'y',
  duration = durations.flip,
  className,
}: FlipCardProps) {
  const reduced = useReducedMotion();
  const target =
    axis === 'y' ? { rotateY: flipped ? 180 : 0 } : { rotateX: flipped ? 180 : 0 };
  const backStatic = axis === 'y' ? 'rotateY(180deg)' : 'rotateX(180deg)';

  if (reduced) {
    return (
      <div className={cx('relative', className)} data-flipped={flipped}>
        <div
          aria-hidden={flipped || undefined}
          className={cx('h-full w-full', flipped && 'invisible')}
        >
          {front}
        </div>
        <div
          aria-hidden={!flipped || undefined}
          className={cx('absolute inset-0', !flipped && 'invisible')}
        >
          {back}
        </div>
      </div>
    );
  }

  return (
    <div className={cx('relative [perspective:800px]', className)} data-flipped={flipped}>
      <motion.div
        className="relative h-full w-full [transform-style:preserve-3d]"
        initial={false}
        animate={target}
        transition={{ duration: duration / 1000, ease: easings.fun }}
      >
        <div
          aria-hidden={flipped || undefined}
          className="absolute inset-0 [backface-visibility:hidden]"
        >
          {front}
        </div>
        <div
          aria-hidden={!flipped || undefined}
          className="absolute inset-0 [backface-visibility:hidden]"
          style={{ transform: backStatic }}
        >
          {back}
        </div>
      </motion.div>
    </div>
  );
}
